import { Component, signal, computed, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from '../navbar/navbar.component';
import { Router } from '@angular/router';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { StatusTabsComponent } from '../growers/status-tabs.component';
import { UiStateService } from '../shared/ui-state.service';
import { TransloadingPassportCellComponent } from './transloading-passport-cell.component';
import { SyncService } from '../shared/sync.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { environment } from '../../environments/environment';
import { 
  PassportMainWithDraftDto 
} from '../interfaces/api-responses';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { HistoryPanelService } from '../shared/history-panel.service';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { FormsModule } from '@angular/forms';
import { DesktopStatusRowComponent, FormattedDateComponent, ActionButtonsComponent, DesktopPaginationComponent } from '../shared/desktop-table-templates.component';
import { DesktopEmptyStateComponent } from '../shared/desktop-empty-state.component';
import { OutgoingPassportService } from '../shared/outgoing-passport.service';
import { OfflineHistoryService } from '../shared/offline-history.service';
import { ScrollPositionService } from '../shared/scroll-position.service';
import { ToastService } from '../shared/toast.service';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearTransloadingListRestoreState,
  hasTransloadingListSnapshot,
  parseTransloadingListSnapshot,
  peekTransloadingExpectRestore,
  saveTransloadingListSnapshot,
  type TransloadingListSnapshot
} from './transloading-list-state.storage';

/** Desktop "Show rows" values — must stay in sync with app-desktop-status-row options */
const TRANSLOADING_DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

function normalizeTransloadingDesktopShowRows(size: number): number {
  if (TRANSLOADING_DESKTOP_SHOW_ROWS_OPTIONS.has(size)) {
    return size;
  }
  return 10;
}

@Component({
  selector: 'app-transloading',
  standalone: true,
  imports: [HeaderComponent, StatusTabsComponent, TransloadingPassportCellComponent, CommonModule, NavbarComponent, LoadingSpinnerComponent, DateWithOrdinalPipe, NoPassportsComponent, FormsModule, DesktopStatusRowComponent, FormattedDateComponent, ActionButtonsComponent, DesktopPaginationComponent, DesktopEmptyStateComponent],
  templateUrl: './transloading.component.html',
  styleUrl: './transloading.component.css'
})
export class TransloadingComponent implements OnInit, OnDestroy {
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;
  
  data: unknown = null;
  transloadFilters: Array<{ id: string; label: string; options: string[] }> = [];
  isLoading = signal(false);
  /** Tracks whether this is the first data load (login / persona switch).
   *  While true, ALL offline-caching operations are awaited so the spinner
   *  stays visible until IndexedDB is fully populated. After the initial
   *  load completes it is set to false so subsequent reloads (syncComplete$,
   *  pull-to-refresh, coming back online) use fire-and-forget caching. */
  isFirstLoad = true;
  private offlineCacheInProgress = false;
  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly historyPanelService = inject(HistoryPanelService);
  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly offlineHistoryService = inject(OfflineHistoryService);
  private readonly scrollPositionService = inject(ScrollPositionService);
  private readonly toastService = inject(ToastService);
  
  /** Subscription for sync completion events to auto-refresh data */
  private syncSubscription?: Subscription;

  // Desktop View State
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  currentPage: number = 1;
  itemsPerPage: number = 10;
  rowsDropdownOpen = false;
  Math = Math;
  private readonly desktopBreakpoint = 768;
  isDesktopView = false;

  /** When set, applied after passports load (returning from passport details). */
  private pendingTransloadingRestore: TransloadingListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;

  toggleRowsDropdown(event?: Event): void {
    if (event) event.stopPropagation();
    this.rowsDropdownOpen = !this.rowsDropdownOpen;
  }

  setItemsPerPage(count: number): void {
    this.itemsPerPage = count;
    this.currentPage = 1;
    this.rowsDropdownOpen = false;
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.rowsDropdownOpen = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDesktopViewState();
  }

  protected updateDesktopViewState(force = false): void {
    const nextValue = globalThis.window === undefined ? false : globalThis.window.innerWidth >= this.desktopBreakpoint;
    const shouldUpdate = force || nextValue !== this.isDesktopView;
    if (shouldUpdate) {
      const wasDesktop = this.isDesktopView;
      this.isDesktopView = nextValue;
      this.applyDesktopViewConstraints();
      if (this.isDesktopView && (force || !wasDesktop)) {
        this.applyDesktopDefaultSort();
      }
      this.rebuildFilters();
    }
  }

  private applyDesktopDefaultSort(): void {
    if (!this.sortColumn) {
      this.sortColumn = 'scheduled';
      this.sortDirection = 'asc';
    }
  }

  private applyDesktopViewConstraints(): void {
    if (this.isDesktopView && this.ui.statusSelected() === 'outgoing') {
      this.ui.setStatusSelected('pending');
      this.currentPage = 1;
    }
  }

  private shouldUseOfflineFeatures(): boolean {
    return !this.isDesktopView;
  }

  get sortedPaginatedRecords(): PassportRecord[] {
    let records = [...this.visibleRecords()];

    // Sorting
    if (this.sortColumn) {
      records.sort((a, b) => {
        const detailsA = this.detailsStore.get(a.id);
        const detailsB = this.detailsStore.get(b.id);
        const { valA, valB } = this.helper.getSortValues(this.sortColumn, a, b, detailsA, detailsB);
        return this.helper.compareValues(valA, valB, this.sortDirection);
      });
    }

    // Pagination
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return records.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
  }

  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  onPageChange(page: number): void {
    const maxPages = this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
    if (page >= 1 && page <= maxPages) {
      this.currentPage = page;
    }
  }

  openPassportDetails(id: string): void {
    this.ui.setExpandedRecord(id);

    // Capture original snapshot for offline history comparison (mobile view only)
    if (this.shouldUseOfflineFeatures()) {
      const originalData = this.detailsStore.get(id);
      if (originalData) {
        this.offlineHistoryService.setOriginalSnapshot(id, originalData);
      }
    }

    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const tabRaw = this.ui.statusSelected();
    const tab: TransloadingListSnapshot['tab'] =
      tabRaw === 'pending' || tabRaw === 'outgoing' || tabRaw === 'completed' ? tabRaw : 'pending';

    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    const showRowsPerPage = normalizeTransloadingDesktopShowRows(this.itemsPerPage);

    saveTransloadingListSnapshot({
      tab,
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: showRowsPerPage,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY
    });

    this.scrollPositionService.saveScrollPosition('/transloading');
    void this.router.navigate(['/passport', id], { state: { from: 'transloading' } });
  }

  /**
   * Build header object for comments/history panels
   */
  private buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    const details = this.detailsStore.get(id);
    return this.helper.buildPanelHeader(details, this.dateTimeHelper, false);
  }

  openHistory(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.historyPanelService.openHistoryPanel(id, header);
    }
  }

  openComments(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.commentsPanelService.openCommentsPanel(id, header);
    }
  }

  onStatusTabSelect(status: 'pending' | 'outgoing' | 'completed'): void {
    this.ui.setStatusSelected(status);
    this.currentPage = 1; // Reset page when switching tabs
    // Regenerate filters based on the new tab's records
    this.rebuildFilters();
  }

  constructor(
    private readonly http: HttpClient, 
    public searchService: SearchService, 
    public filterService: FilterService, 
    public passports: PassportService, 
    public ui: UiStateService, 
    public detailsStore: PassportDetailsStore,
    private readonly router: Router,
    private readonly offlineStorage: OfflineStorageService,
    public syncService: SyncService,
    public commentsNotification: CommentsNotificationService,
    private readonly helper: PassportHelperService,
    private readonly personaRouting: PersonaRoutingService,
    private readonly outgoingPassportService: OutgoingPassportService
  ) {}

  ngOnInit(): void {
    let restoring = peekTransloadingExpectRestore();
    let snapshot = restoring ? parseTransloadingListSnapshot() : null;
    if (restoring && !snapshot) {
      clearTransloadingListRestoreState();
      restoring = false;
      snapshot = null;
    }
    if (!restoring && hasTransloadingListSnapshot()) {
      clearTransloadingListRestoreState();
    }
    this.pendingTransloadingRestore = snapshot;

    if (!restoring) {
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
    }

    // Set persona in localStorage so passport details page knows we're in Transloading
    localStorage.setItem('persona', 'transloader');

    this.updateDesktopViewState(true);

    // Subscribe to sync completion events to auto-refresh data after coming back online (mobile only)
    if (this.shouldUseOfflineFeatures()) {
      this.syncSubscription = this.syncService.syncComplete$.subscribe(() => {
        console.log('📥 Sync completed - refreshing passport data');
        void this.loadPassportsFromAPI();
      });
    }

    // Check if we should skip reload (returning from detail in mobile view)
    if (this.shouldSkipDataReload()) {
      this.scrollPositionService.restoreScrollPositionAfterDataLoad('/transloading');
      this.commentsNotification.initialize();
      void this.loadPassportsFromAPI({ silent: true, preserveScrollRoute: '/transloading' });
      return;
    }

    void (async () => {
      await this.loadPassportsFromAPI();

      // Initial sync is complete – all subsequent loads use fire-and-forget caching
      this.isFirstLoad = false;

      if (this.pendingTransloadingRestore) {
        this.applyTransloadingListRestore(this.pendingTransloadingRestore);
        this.pendingTransloadingRestore = null;
      }

      this.scrollPositionService.restoreScrollPositionAfterDataLoad('/transloading');
      this.tryScrollRestoredPassportIntoView();

      // Initialize comments notification service to ensure count is displayed
      this.commentsNotification.initialize();
      
      // Check for outgoing records and sync if online (mobile only)
      if (this.shouldUseOfflineFeatures() && navigator.onLine) {
        const outgoingCount = this.passports.records().filter(r => r.status === 'outgoing' && !r.isSynced).length;
        if (outgoingCount > 0) {
          console.log(`Found ${outgoingCount} outgoing records - triggering sync`);
          await this.syncService.syncOutgoingRecords();
        }
        
        // Also check for drafts to save when online
        const drafts = await this.offlineStorage.getAllDrafts();
        if (drafts.length > 0) {
          console.log(`Found ${drafts.length} draft(s) - saving when online`);
          await this.syncService.submitDraftsWhenOnline();
        }
      }
    })();
  }

  /**
   * Check if we should skip data reload (silent return from detail page)
   * Returns true if:
   * 1. We're in mobile view (where scroll preservation matters)
   * 2. Data already exists in the passport service
   * 3. We're returning from a detail page (not a fresh app load)
   */
  private shouldSkipDataReload(): boolean {
    if (peekTransloadingExpectRestore()) {
      return false;
    }
    // Only skip in mobile view
    if (this.isDesktopView) return false;
    
    // Only skip if data already exists
    if (this.passports.records().length === 0) return false;
    
    // Only skip if returning from detail page
    return this.scrollPositionService.isReturning();
  }

  private applyTransloadingListRestore(snapshot: TransloadingListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setStatusSelected(snapshot.tab);
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    this.itemsPerPage = normalizeTransloadingDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.applyDesktopViewConstraints();
    const desktopOutgoingMappedToPending =
      this.isDesktopView && snapshot.tab === 'outgoing' && this.ui.statusSelected() === 'pending';
    this.currentPage = desktopOutgoingMappedToPending ? 1 : snapshot.currentPage;
    const totalPages = this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
    if (this.currentPage > totalPages) {
      this.currentPage = Math.max(1, totalPages);
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.rebuildFilters();
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearTransloadingListRestoreState();
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  ngOnDestroy(): void {
    this.searchService.cancelDebouncedFilterUpdate();
    this.syncSubscription?.unsubscribe();
  }

  filteredAllStatusRecords = computed(() => {
    try {
      const query = (this.searchService.filterQuery() || '').trim().toLowerCase();
      const hasQuery = query.length > 0;
      const activeFilters = this.filterService.filters();
      const apnSelected = (activeFilters['apnName'] || []);
      const destSelected = (activeFilters['dest'] || []);
      const origSelected = (activeFilters['orig'] || []);

      const matchesFilters = (d: ReturnType<PassportDetailsStore['get']>): boolean => {
        // For APN filter, check both apnName and apn fields
        const apnValue = d?.apnName || d?.apn || '';
        if (apnSelected.length && !(apnSelected.includes(apnValue))) return false;
        // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
        const destinationValue = d?.destinationWeighbridge || d?.destination || '';
        if (destSelected.length && !(destSelected.includes(destinationValue))) return false;
        // Handle "No Origin Field" for origin filter
        if (origSelected.length) {
          const originValue = d?.origin?.trim() || '';
          // If "No Origin Field" is selected and origin is empty, it matches
          if (origSelected.includes(this.helper.NO_ORIGIN_FIELD_TEXT) && !originValue) {
            // Match - empty origin matches "No Origin Field"
          } else if (!origSelected.includes(originValue)) {
            return false;
          }
        }
        return true;
      };

      return this.passports.records().filter(rec => {
        const det = this.detailsStore.get(rec.id);
        
        // Apply active filters
        if (!matchesFilters(det)) return false;
        
        // Apply search query
        if (!hasQuery) return true;
        
        // Use "No Origin Field" as searchable text when origin is empty
        const originSearchValue = det?.origin?.trim() ? det.origin : this.helper.NO_ORIGIN_FIELD_TEXT;
        
        const searchable = [
          rec.id || '',
          det?.loadNumber || '',
          det?.variety || '',
          det?.vendorName || '',
          det?.vendorNumber || '',
          det?.apnName || '',
          det?.haulier || '',
          // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
          det?.destinationWeighbridge || det?.destination || '',
          originSearchValue,
          det?.bulkerMain || det?.bulkerNumber || '', // Search both bulker fields
          det?.authorisedBy || '',
          det?.haccpCheck || '',
          // Format scheduled date for search
          this.dateTimeHelper.formatScheduledText(det?.dueDateTime || '')
        ];
        
        const haystack = searchable.join(' ').toLowerCase();
        return haystack.includes(query);
      });
    } catch { return []; }
  });

  pendingCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'pending').length;
    } catch { return 0; }
  });

  outgoingCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'outgoing').length;
    } catch { return 0; }
  });

  completedCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'completed').length;
    } catch { return 0; }
  });

  visibleRecords = computed(() => {
    try {
      const selectedStatus = this.ui.statusSelected() || 'pending';
      const filtered = this.filteredAllStatusRecords().filter(rec => rec.status === selectedStatus);

      return [...filtered].sort((a, b) => {
        const detailsA = this.detailsStore.get(a.id);
        const detailsB = this.detailsStore.get(b.id);
        const dateA = detailsA?.dueDateTime || detailsA?.dateTime || '';
        const dateB = detailsB?.dueDateTime || detailsB?.dateTime || '';
        return dateA.localeCompare(dateB);
      });
    } catch { return []; }
  });
  trackById = PassportService.trackById;

  async onRefresh(): Promise<void> { 
    // DEBUG: Show current IndexedDB state at start of refresh
    if (this.shouldUseOfflineFeatures()) {
      try {
        const outgoing = await this.offlineStorage.getAllOutgoingPassports();
        const drafts = await this.offlineStorage.getAllDrafts();
        const cached = await this.offlineStorage.getAllPassports();
        this.toastService.info(
          `📊 State: ${outgoing.length} outgoing, ${drafts.length} drafts, ${cached.length} cached`,
          4000
        );
      } catch (e) {
        console.warn('Failed to get debug storage info:', e);
      }
    }

    // Sync outgoing records first if online (mobile only)
    if (this.shouldUseOfflineFeatures() && navigator.onLine) {
      const outgoingCount = this.passports.records().filter(r => r.status === 'outgoing' && !r.isSynced).length;
      if (outgoingCount > 0) {
        console.log(`Syncing ${outgoingCount} outgoing records before refresh`);
        await this.syncService.syncOutgoingRecords();
      }
    }
    
    await this.loadPassportsFromAPI();
    // Refresh comment notification count
    await this.commentsNotification.refresh();
  }

  onNavChange(id: string): void {
    this.personaRouting.handleNavigation(id, 'transloading');
  }

  private async loadPassportsFromAPI(options?: { silent?: boolean; preserveScrollRoute?: string }): Promise<void> {
    if (this.isLoading()) return;

    const isSilent = options?.silent === true;
    const preserveScrollRoute = options?.preserveScrollRoute;

    if (!isSilent) {
      this.isLoading.set(true);
    }

    // Only save scroll position when NOT silent - silent refresh should preserve existing position
    if (preserveScrollRoute && !isSilent) {
      this.scrollPositionService.saveScrollPosition(preserveScrollRoute);
    }

    const isOnline = navigator.onLine;

    try {
      const authData = this.getAuthenticationData();
      
      if (!authData.token) {
        await this.handleNoAuthentication();
        return;
      }

      const roleId = this.helper.getRoleIdForPersona(authData.persona);
      console.log(`Loading passports for persona '${authData.persona}' with roleId ${roleId}`);

      if (!isOnline) {
        await this.handleOfflineMode();
        return;
      }

      await this.fetchAndProcessPassports(authData, roleId);
    } catch (error) {
      await this.handleLoadError(error);
    } finally {
      if (!isSilent) {
        this.isLoading.set(false);
      }
      // Only restore scroll position when NOT silent - silent refresh should preserve existing position
      if (preserveScrollRoute && !isSilent) {
        this.scrollPositionService.restoreScrollPositionAfterDataLoad(preserveScrollRoute);
      }
    }
  }

  /**
   * Get the persona name for this component
   * Always returns 'transloader' to ensure correct roleId regardless of localStorage
   */
  private get personaName(): string {
    return 'transloader';
  }

  private getAuthenticationData() {
    // FIX: Use component's personaName instead of localStorage to ensure correct roleId
    // This prevents issues when user navigates directly via URL (localStorage might have old persona)
    return {
      userID: localStorage.getItem('userID'),
      persona: this.personaName, // Always use 'transloader' for transloading component
      token: localStorage.getItem('accessToken'),
      tokenType: localStorage.getItem('tokenType') || 'Bearer'
    };
  }

  private async handleNoAuthentication(): Promise<void> {
    console.warn('No authentication token found');
    if (this.shouldUseOfflineFeatures()) {
      await this.loadFromOfflineStorage();
      this.data = { message: 'Using offline data (no authentication token)' };
    } else {
      this.data = { message: 'No authentication token found' };
    }
  }

  private async handleOfflineMode(): Promise<void> {
    if (this.shouldUseOfflineFeatures()) {
      console.log('Device is offline - loading from IndexedDB');
      await this.loadFromOfflineStorage();
      this.data = { message: 'Offline mode - using cached data' };
    } else {
      console.log('Desktop view detected - skipping offline cache load');
      this.data = { message: 'Offline mode is available on mobile devices only' };
    }
  }

  private async fetchAndProcessPassports(authData: any, roleId: number): Promise<void> {
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${authData.userID}&roleId=${roleId}`;
    const headers = { 'Authorization': `${authData.tokenType} ${authData.token}`, 'Content-Type': 'application/json' };
    
    console.log(`Fetching passports from: ${apiUrl}`);
    const passports = await firstValueFrom(this.http.get<PassportMainWithDraftDto[]>(apiUrl, { headers }));
    console.log(`API Response: ${passports.length} passports received`);

    // Map passports to frontend records with status based on statusCode
    const frontendRecords = this.mapPassportsToRecords(passports, true);
    
    // Preserve unsynced outgoing records from being overwritten during refresh
    const currentRecords = this.passports.records();
    const preservedOutgoing = currentRecords.filter(r => 
      r.status === 'outgoing' && !r.isSynced
    );
    
    // Remove duplicates: API data takes precedence unless record is outgoing
    const preservedIds = new Set(preservedOutgoing.map(r => r.id));
    const nonConflictingApiRecords = frontendRecords.filter(r => !preservedIds.has(r.id));
    
    // Merge preserved outgoing records with non-conflicting API records
    this.passports.records.set([...preservedOutgoing, ...nonConflictingApiRecords]);

      // Store important details for cells
      this.storePassportDetails(passports);
      
      // Load outgoing passports from IndexedDB and merge with regular passports
      await this.loadAndMergeOutgoingPassports();
      
      this.rebuildFilters();
      
      this.data = {
        message: `Loaded ${passports.length} passports`,
        totalCount: passports.length
      };
      
      console.log(`Successfully loaded ${passports.length} passports for user ${authData.userID}`);

      // Cache all data for offline use.
      // Origins are NOT cached for transloading (origin field is read-only from passport data).
      if (this.shouldUseOfflineFeatures() && authData.persona.toLowerCase().includes('transload')) {
        const cacheHeaders = { 'Authorization': `${authData.tokenType} ${authData.token}`, 'Content-Type': 'application/json' };

        if (this.isFirstLoad) {
          // First load: await ALL caching so spinner stays visible until
          // IndexedDB is fully populated with passports, comments, history
          // and authorised users.
          await Promise.all([
            this.cacheAllDataForOffline(passports, roleId, cacheHeaders).catch(err =>
              console.warn('Background offline caching failed:', err)
            ),
            this.cacheAuthorisedUsers(cacheHeaders, passports).catch(err =>
              console.warn('Background authorised users caching failed:', err)
            )
          ]);
        } else {
          // Subsequent loads: fire-and-forget (current behavior)
          void this.cacheAllDataForOffline(passports, roleId, cacheHeaders).catch(err =>
            console.warn('Background offline caching failed:', err)
          );
          void this.cacheAuthorisedUsers(cacheHeaders, passports).catch(err =>
            console.warn('Background authorised users caching failed:', err)
          );
        }
      }
  }

  /**
   * Map passports to frontend records with status based on statusCode
   */
  private mapPassportsToRecords(passports: PassportMainWithDraftDto[], isSynced: boolean): PassportRecord[] {
    return passports.map(p => {
      // If statusCode is "IN_TRANSLOADING_PROCESS" or "AWAITING_GROWER_ACTION", set status to 'pending'
      // Otherwise, set to 'completed' (case-insensitive check)
      const statusCodeUpper = (p.statusCode || '').toUpperCase();
      const status = (statusCodeUpper === 'IN_TRANSLOADING_PROCESS' || statusCodeUpper === 'AWAITING_GROWER_ACTION') ? 'pending' : 'completed';
      return { 
        id: p.passportID.toString(), 
        status: status, 
        isSynced: isSynced 
      };
    });
  }

  /**
   * Build passport details object from API or cached passport data
   */
  private buildPassportDetails(p: PassportMainWithDraftDto, additionalFields?: { comments?: any[]; history?: any[] }): any {
    return {
      id: p.passportID.toString(),
      loadNumber: p.loadNumber || '',
      dateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
      vendorName: p.vendorName || '',
      vendorNumber: p.vendorNumber || '',
      apn: p.apn || '',
      apnName: p.apnName || '',
      variety: p.variety || '',
      destination: p.destination || '',
      destinationWeighbridge: p.destinationWeighbridge || '',
      origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
      bulkerNumber: p.trailerNumber || '',
      bulkerMain: p.trailerMain || '',
      dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
      loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
      haccpCheck: (p.haccpCheck === true ? 'Yes' : 'No'),
      haccpImages: [],
      haccpImageLinks: p.haccpImageLinks || [],
      imageCount: p.imageCount || 0,
      deletedImageFilenames: [],
      haccpImageFiles: [],
      authorisedBy: p.authorizerName || '',
      plantID: p.plantID,
      plannedDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.plannedDateTime || ''),
      isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',
      hasSeenPcrChanges: p.hasSeenPcrChanges || false,
      isPassportCompleted: p.isPassportCompleted || false,
      loadStatusName: p.loadStatusName || '',
      ...(additionalFields?.comments && { comments: additionalFields.comments }),
      ...(additionalFields?.history && { history: additionalFields.history })
    };
  }

  private storePassportDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      this.detailsStore.upsert(this.buildPassportDetails(p));
    }
  }


  private async handleLoadError(error: any): Promise<void> {
    console.warn('Failed to load passports from API:', error);
    console.warn('Error details:', {
      status: error?.status,
      message: error?.message,
      error: error?.error
    });
    
    const isOnline = navigator.onLine;
    
    // When online, show error message without fallback to cache
    if (isOnline) {
      this.data = this.getErrorMessage(error);
      return;
    }
    
    // When offline, try loading from offline storage if enabled
    if (this.shouldUseOfflineFeatures()) {
      await this.loadFromOfflineStorage();
      
      if (this.passports.records().length === 0) {
        // Show error if offline storage is also empty
        this.data = this.getErrorMessage(error);
      } else {
        this.data = { message: 'Using offline cached data' };
      }
    } else {
      this.data = { message: 'Offline mode is available on mobile devices only' };
    }
  }

  private getErrorMessage(error: any): { message: string } {
    if (error.status === 401) {
      return { message: 'Authentication failed - please login again' };
    }
    if (error.status === 403) {
      return { message: 'Permission denied' };
    }
    return { message: 'Failed to load passports' };
  }

  /**
   * Load passports from offline storage (IndexedDB)
   */
  private async loadFromOfflineStorage(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) return;
    try {
      const cachedPassports = await this.offlineStorage.getAllPassports();
      console.log(`Loaded ${cachedPassports.length} passports from offline storage`);
      
      if (cachedPassports.length === 0) {
        console.log('No cached passports found in offline storage');
        return;
      }

      // Map cached passports to frontend records with status based on statusCode
      const frontendRecords = this.mapPassportsToRecords(cachedPassports, false);
      this.passports.records.set(frontendRecords);

      // Load details for each cached passport, including comments and history
      for (const p of cachedPassports) {
        const passportId = p.passportID;
        
        // Load comments and history from IndexedDB for this passport
        // Helper functions handle IndexedDB connection errors gracefully
        const cachedComments = await this.safeLoadComments(passportId);
        const cachedHistory = await this.safeLoadHistory(passportId);
        
        this.detailsStore.upsert(this.buildPassportDetails(p, {
          comments: cachedComments || [],
          history: cachedHistory || []
        }));
        
        // Image caching removed: do not load images from offline storage
      }
      
      console.log(`✅ Loaded comments and history for ${cachedPassports.length} passports from IndexedDB`);

      // Load outgoing passports from IndexedDB and merge with regular passports
      await this.loadAndMergeOutgoingPassports();
      
      this.rebuildFilters();
    } catch (error) {
      console.warn('Error loading from offline storage:', error);
    }
  }

  /**
   * Load outgoing passports from IndexedDB and merge with regular passports
   * Delegates to OutgoingPassportService to avoid code duplication
   */
  private async loadAndMergeOutgoingPassports(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) return;
    await this.outgoingPassportService.mergeOutgoingPassportsWithRecords(
      this.passports,
      this.ui,
      this.detailsStore,
    );
  }

  /**
   * Cache all data (passports, comments, history, images) for offline use
   */
  private async cacheAllDataForOffline(passports: PassportMainWithDraftDto[], roleId: number, headers: any): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) return;
    if (this.offlineCacheInProgress) {
      console.log('Offline caching already in progress, skipping...');
      return;
    }

    this.offlineCacheInProgress = true;

    try {
      console.log(`Starting offline cache for ${passports.length} passports...`);

      // Clear old persona data before caching new data
      await this.offlineStorage.clearPersonaData();
      console.log('Cleared old persona data from IndexedDB');

      // Save all passports to IndexedDB
      await this.offlineStorage.saveAllPassports(passports);
      console.log('Main passport data cached successfully');

      // Process passports in true batches - 5 passports in parallel at a time
      const batchSize = 5;
      for (let i = 0; i < passports.length; i += batchSize) {
        const batch = passports.slice(i, i + batchSize);
        
        // Create and execute promises for THIS batch only
        await Promise.all(batch.map(async (passport) => {
          const passportId = passport.passportID;
          try {
            await this.cacheCommentsForPassport(passportId, headers);
            await this.cacheHistoryForPassport(passportId, headers);
          } catch (err) {
            console.warn(`Failed to cache data for passport ${passportId}:`, err);
          }
        }));
        
        console.log(`Cached batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(passports.length / batchSize)}`);
      }

      // Save last sync time
      await this.offlineStorage.setMetadata('lastSync', new Date().toISOString());

      // Clean up stale passports that are no longer in the API response
      const validIds = passports.map(p => p.passportID.toString());
      const removedCount = await this.offlineStorage.cleanupStalePassports(validIds);
      if (removedCount > 0) {
        this.toastService.info(`Removed ${removedCount} outdated cached passports`, 3000);
      }

      console.log('Offline caching completed successfully');
    } catch (error) {
      console.warn('Error during offline caching:', error);
    } finally {
      this.offlineCacheInProgress = false;
    }
  }

  /**
   * Cache comments for a specific passport
   */
  private async cacheCommentsForPassport(passportId: number, headers: any): Promise<void> {
    const userId = Number.parseInt(localStorage.getItem('userID') || '0', 10);
    const body = {
      PassportId: passportId,
      UserId: userId
    };
    await this.cachePassportData(
      passportId,
      headers,
      'comments',
      `${environment.apiBaseUrl}/api/v1/passports/comments`,
      body,
      (id, data) => this.offlineStorage.saveComments(id, data),
      'comments'
    );
  }

  /**
   * Cache history for a specific passport
   */
  private async cacheHistoryForPassport(passportId: number, headers: any): Promise<void> {
    await this.cachePassportData(
      passportId,
      headers,
      'history',
      `${environment.apiBaseUrl}/api/v1/passports/history?userId=${localStorage.getItem('userID')}&passportId=${passportId}`,
      null, // History still uses GET, so body is null
      (id, data) => this.offlineStorage.saveHistory(id, data),
      'history'
    );
  }

  /**
   * Generic helper method to cache passport data (comments/history)
   */
  private async cachePassportData(
    passportId: number,
    headers: any,
    dataType: 'comments' | 'history',
    url: string,
    body: any,
    saveFn: (id: number, data: any[]) => Promise<void>,
    errorLabel: string
  ): Promise<void> {
    try {
      const data = dataType === 'comments'
        ? await firstValueFrom(this.http.post<any[]>(url, body, { headers }))
        : await firstValueFrom(this.http.get<any[]>(url, { headers }));
      await saveFn(passportId, data);
    } catch (error: any) {
      if (error?.status === 404) {
        // No data - save empty array
        await saveFn(passportId, []);
      } else {
        console.warn(`Failed to cache ${errorLabel} for passport ${passportId}:`, error);
      }
    }
  }

  /**
   * Cache images for a specific passport
   */
  // Image caching removed

  /**
   * Convert a Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private rebuildFilters(): void {
    try {
      console.log('Rebuilding filters...');
      // Use all records (not filtered) to ensure filter options remain available
      const relevantRecords = this.passports.records();
      
      console.log(`Rebuilding filters from all records. Total: ${relevantRecords.length}`);
      
      const filterOptions = this.collectFilterOptions(relevantRecords);
      
      console.log('Filter options built:', {
        origins: filterOptions.origins.length,
        destinations: filterOptions.destinations.length,
        apnNames: filterOptions.apns.length
      });

      this.transloadFilters = this.buildFilterDefinitions(filterOptions);

    } catch (error) {
      console.warn('Error rebuilding filters:', error);
      this.transloadFilters = this.getEmptyFilterDefinitions();
    }
  }

  private collectFilterOptions(records: any[]): { origins: string[]; destinations: string[]; apns: string[]; hasEmptyOrigin: boolean } {
    const originOptions: string[] = [];
    const destinationOptions: string[] = [];
    const apnOptions: string[] = [];
    let hasEmptyOrigin = false;

    let index = 0;
    for (const rec of records) {
      const details = this.detailsStore.get(rec.id);
      
      if (details) {
        // Check if this record has an empty origin
        if (details.origin?.trim()) {
          this.helper.addUniqueOption(originOptions, details.origin);
        } else {
          hasEmptyOrigin = true;
        }
        // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
        const destinationValue = details.destinationWeighbridge || details.destination;
        this.helper.addUniqueOption(destinationOptions, destinationValue);
        // Prefer APN Name, fallback to APN code
        const apnValue = details.apnName || details.apn;
        this.helper.addUniqueOption(apnOptions, apnValue);
      }
      index++;
    }

    return {
      origins: originOptions,
      destinations: destinationOptions,
      apns: apnOptions,
      hasEmptyOrigin
    };
  }

  private buildFilterDefinitions(options: { origins: string[]; destinations: string[]; apns: string[]; hasEmptyOrigin: boolean }): any[] {
    // Sort origin options, keeping "No Origin Field" first if applicable
    const sortedOrigins = this.sortOptions(options.origins);
    const originOptionsWithNoOrigin = options.hasEmptyOrigin 
      ? [this.helper.NO_ORIGIN_FIELD_TEXT, ...sortedOrigins] 
      : sortedOrigins;

    return [
      { id: 'apnName', label: 'APN/Name', options: this.sortOptions(options.apns) },
      { id: 'dest', label: 'Destination', options: this.sortOptions(options.destinations) },
      { id: 'orig', label: 'Origin', options: originOptionsWithNoOrigin }
    ];
  }

  private sortOptions(options: string[]): string[] {
    return [...options].sort((a, b) => a.localeCompare(b));
  }

  private getEmptyFilterDefinitions(): any[] {
    return [
      { id: 'apnName', label: 'APN/Name', options: [] },
      { id: 'dest', label: 'Destination', options: [] },
      { id: 'orig', label: 'Origin', options: [] }
    ];
  }

  /**
   * Determine the visual style state for a passport based on its properties
   */
  getStyleState(passportId: string): 'default' | 'pcr-change' | 'cancelled' | 'state-2' {
    const details = this.detailsStore.get(passportId);
    return this.helper.getStyleState(details);
  }

  // Shunting yards caching removed - only needed for Gatehouse and Scheduler personas

  /**
   * Cache authorised users for offline use
   */
  private async cacheAuthorisedUsers(headers: any, passports: PassportMainWithDraftDto[]): Promise<void> {
    try {
      if (!passports || passports.length === 0) {
        console.warn('No passports available, skipping authorised users cache');
        return;
      }
      
      const userID = localStorage.getItem('userID');
      if (!userID) {
        console.warn('No userID found, skipping authorised users cache');
        return;
      }
      
      // Use passportId in API call to get authorizers for this transloading station
      const firstPassportId = passports[0].passportID;
      const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?passportId=${firstPassportId}`;
      console.log('Fetching authorised users from:', apiUrl);
      
      const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));
      console.log('Authorised users API response:', response);
      
      if (response?.authorisedUsers) {
        // Deduplicate authorised users by authorizerId before storing
        const seen = new Map<number, any>();
        for (const user of response.authorisedUsers) {
          const key = user.authorizerId ?? user.id;
          if (key != null && !seen.has(key)) {
            seen.set(key, user);
          }
        }
        const uniqueUsers = Array.from(seen.values());
        // Store with userId as key for consistent retrieval across the app
        await this.offlineStorage.saveAuthorisedUsers(Number.parseInt(userID, 10), uniqueUsers);
        console.log(`Cached ${uniqueUsers.length} unique authorised users (${response.authorisedUsers.length} total from API)`);
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        console.warn('Failed to cache authorised users:', error);
      }
    }
  }

  /**
   * Safely load comments from IndexedDB, returning empty array on error
   */
  private async safeLoadComments(passportId: number): Promise<any[]> {
    return this.helper.safeLoadPassportData(passportId, 'comments', (id) => this.offlineStorage.getComments(id));
  }

  /**
   * Safely load history from IndexedDB, returning empty array on error
   */
  private async safeLoadHistory(passportId: number): Promise<any[]> {
    return this.helper.safeLoadPassportData(passportId, 'history', (id) => this.offlineStorage.getHistory(id));
  }
}


